import { startOfDay, endOfDay, isSameDay } from 'date-fns';
import { SHEET_EVENT } from 'subscriptions/events/events.enum';
import { subscriptionsFabric } from 'subscriptions/subscriptions.fabric';
import workerSchedulePageActions from 'store/workerSchedule/workerSchedulePageActions';
import { fetchFullScheduleSheets } from 'store/workerSchedule/workerSchedulePageOperations';
import { getSheetResourceDictionaries, getSheetResourceUpdates } from 'subscriptions/utils';
import { hasPermissionsFor } from 'helpers/_helpers';

const refetchFullSchedule = (store) => {
  const { personalProfile, workerSchedule } = store.getState();
  const isFieldTechnician = personalProfile?.user?.profile?.role?.roleName === 'Field Technician';
  // same access check as the Full Schedule tab; the endpoint rejects anyone else
  if (!hasPermissionsFor(isFieldTechnician ? 'myScheduleFull' : 'activeSchedule')) return;

  return store.dispatch(fetchFullScheduleSheets(workerSchedule.fullSchedule.selectedDate));
};

const handlers = {
  [SHEET_EVENT.created]: (store) => (data) => {
    const { selectedDate } = store.getState().workerSchedule.fullSchedule;

    if (data.published && isSameDay(new Date(data.createdAt), selectedDate)) {
      store.dispatch(workerSchedulePageActions.addFullScheduleSheet(data));
    }
  },
  [SHEET_EVENT.updated]: (store) => (data) => {
    const { selectedDate, sheets } = store.getState().workerSchedule.fullSchedule;
    if (!sheets.some((sheet) => sheet._id === data?._id)) return;

    const action = isSameDay(new Date(data.createdAt), selectedDate)
      ? workerSchedulePageActions.putFullScheduleSheet
      : workerSchedulePageActions.deleteFullScheduleSheet;
    store.dispatch(action(data));
  },
  [SHEET_EVENT.resourcesAvailability]: (store) => (data) => {
    const sheets = store.getState().workerSchedule.fullSchedule.sheets;
    const date = store.getState().workerSchedule.fullSchedule.selectedDate;

    if (
      new Date(data.from)?.getTime() > endOfDay(date).getTime() ||
      new Date(data.to)?.getTime() < startOfDay(date).getTime()
    ) return;

    const { updatedWorkersBySheet, updatedEquipmentBySheet } = getSheetResourceDictionaries(
      sheets,
      data.workers,
      data.equipment
    );

    for (const sheet of sheets) {
      const sheetUpdatedWorkers = updatedWorkersBySheet[sheet._id];
      const sheetUpdatedEquipment = updatedEquipmentBySheet[sheet._id];
      if (!sheetUpdatedWorkers?.workers?.length && !sheetUpdatedEquipment?.equipment?.length) continue;
      
      const { workers, equipment } = getSheetResourceUpdates(sheet, sheetUpdatedWorkers?.workers, sheetUpdatedEquipment?.equipment);

      store.dispatch(workerSchedulePageActions.patchFullScheduleSheet({
        ...sheet,
        workers,
        equipment,
      }));
    }
  },
  [SHEET_EVENT.deleted]: (store) => (data) => {
    const sheets = store.getState().workerSchedule.fullSchedule.sheets;
    const sheetInSchedule = sheets.some((sheet) => sheet._id === data?._id);

    if (sheetInSchedule) store.dispatch(workerSchedulePageActions.deleteFullScheduleSheet(data));
  },
  [SHEET_EVENT.notes]: (store) => (data) => {
    const sheets = store.getState().workerSchedule.fullSchedule.sheets;
    const sheetInSchedule = sheets.some((sheet) => sheet._id === data?._id);

    if (sheetInSchedule) {
      store.dispatch(workerSchedulePageActions.patchFullScheduleSheet(data));
    }
  },
  [SHEET_EVENT.updatedCrewLeader]: (store) => (data) => {
    const sheets = store.getState().workerSchedule.fullSchedule.sheets;
    const sheetInSchedule = sheets.some((sheet) => sheet._id === data?._id);

    if (sheetInSchedule) store.dispatch(workerSchedulePageActions.patchFullScheduleSheetWorker(data));
  },
  [SHEET_EVENT.canceled]: (store) => (data) => {
    const sheets = store.getState().workerSchedule.fullSchedule.sheets;
    const sheetInSchedule = sheets.some((sheet) => sheet._id === data?._id);

    if (sheetInSchedule) store.dispatch(workerSchedulePageActions.patchFullScheduleSheet(data));
  },
  [SHEET_EVENT.uncanceled]: (store) => (data) => {
    const sheets = store.getState().workerSchedule.fullSchedule.sheets;
    const sheetInSchedule = sheets.some((sheet) => sheet._id === data?._id);

    if (sheetInSchedule) store.dispatch(workerSchedulePageActions.patchFullScheduleSheet({ ...data, canceledAt: null, canceledBy: null }));
  },
  [SHEET_EVENT.publishedBunch]: (store) => () => refetchFullSchedule(store),
  [SHEET_EVENT.submitted]: (store) => (data) => {
    const sheets = store.getState().workerSchedule.fullSchedule.sheets;
    const sheetInSchedule = sheets.some((sheet) => sheet._id === data?._id);

    if (sheetInSchedule) {
      const submissions = sheetInSchedule.submissions?.map(sub => sub) || [];

      if (data.submission && !submissions?.some(sub => sub._id === data?.submission?._id)) {
        submissions.push(data.submission);
      }

      const updates = {
        _id: data._id,
        notes: data.notes || sheetInSchedule.notes || '',
        submittedAt: data.submittedAt,
        submittedBy: data.submittedBy,
        submissions: submissions,
      };

      store.dispatch(workerSchedulePageActions.patchFullScheduleSheet(updates));
    }
  },
  [SHEET_EVENT.submissionDeleted]: (store) => (data) => {
    const sheets = store.getState().workerSchedule.fullSchedule.sheets;
    const sheetInSchedule = sheets.some((sheet) => sheet._id === data?._id);

    if (sheetInSchedule) {
      let submissions = sheetInSchedule.submissions;
      if (submissions?.length) submissions = submissions.filter(submission => submission._id !== data.submission?._id);

      const updates = {
        _id: data._id,
        notes: data.notes || sheetInSchedule.notes || '',
        submittedAt: data.submittedAt,
        submittedBy: data.submittedBy,
      };
      if (submissions) updates.submissions = submissions.map(sub => sub);

      store.dispatch(workerSchedulePageActions.patchFullScheduleSheet(updates));
    }
  },
};

export const fullScheduleSheetsSubscriptionMiddleware = subscriptionsFabric(
  workerSchedulePageActions,
  SHEET_EVENT,
  handlers,
  { onReconnect: refetchFullSchedule }
);
